// src/Component/chat/component/ChatInsideSidebar.tsx
import { useContext, useEffect, useState } from 'react';
import { ChatContext } from '../../../context/ChatContext/chatContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Icon } from '@iconify/react';
import { FIREBASE_COLLECTIONS } from '../../api/url';
import { getProfile } from '../../../services/profile';
import React from 'react';

interface MemberInfo {
  id: string;
  name: string;
  role: string;
  avatar: string;
  userId: string;
}

const ChatInsideSidebar = () => {
  const { selectedChat, currentUserId } = useContext(ChatContext);
  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [loading, setLoading] = useState(false);

  const getUserInitials = (name: string): string => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  };

  const extractUserId = (fullId: string): string => {
    const match = fullId.match(/\d+$/);
    return match ? match[0] : fullId;
  };

  /**
   * Determine the display role for a member.
   *
   * Multi-nutritionist logic:
   *   - The FIRST nutritionistid_ in the members array = "Assigned Nutritionist"
   *   - Any additional nutritionistid_ entries       = "Nutritionist"
   *   - If the entry is the currently logged-in user, prepend "You"
   */
  const determineRole = (memberId: string, chatGroupData: any): string => {
    if (memberId.startsWith('userid_'))   return 'Client';
    if (memberId.startsWith('adminid_'))  return 'Head Nutritionist';

    if (memberId.startsWith('nutritionistid_')) {
      const membersList: string[]  = chatGroupData?.members || [];
      const allNutritionists       = membersList.filter((m) => m.startsWith('nutritionistid_'));
      const isAssigned             = allNutritionists[0] === memberId;
      const isMe                   = memberId === currentUserId;

      if (isAssigned) {
        return isMe ? 'You (Assigned Nutritionist)' : 'Assigned Nutritionist';
      } else {
        return isMe ? 'You (Nutritionist)' : 'Nutritionist';
      }
    }

    return 'Member';
  };

  const getFallbackName = (memberId: string): string => {
    if (memberId.startsWith('adminid_'))        return 'Admin ' + extractUserId(memberId);
    if (memberId.startsWith('nutritionistid_')) return 'Nutritionist ' + extractUserId(memberId);
    return 'User ' + extractUserId(memberId);
  };

  /** Fetch nutritionist/admin name from backend API */
  const fetchFromAPI = async (numericId: string): Promise<{ name: string; avatar: string } | null> => {
    try {
      const data         = await getProfile(Number(numericId));
      /* GET_PROFILE returns { success, message, data: {...} } — single object.
         Older shapes (nutritionist_data / nutritionists[]) kept as fallbacks. */
      const nutritionist = data.data || data.nutritionist_data || data.nutritionists?.[0];
      if (!nutritionist) return null;

      const name   = `${(nutritionist.nutrist_fname || '').trim()} ${(nutritionist.nutrist_lname || '').trim()}`.trim();
      const avatar = nutritionist.nutrist_profile || '';
      return name ? { name, avatar } : null;
    } catch {
      return null;
    }
  };

  /** Fetch client name from FIREBASE_COLLECTIONS.USERS Firestore collection */
  const fetchClientFromFirestore = async (memberId: string): Promise<{ name: string; avatar: string } | null> => {
    // Try full prefixed ID first (e.g. "userid_11")
    try {
      const userDoc = await getDoc(doc(db, FIREBASE_COLLECTIONS.USERS, memberId));
      if (userDoc.exists()) {
        const d    = userDoc.data();
        const name = `${(d.user_fname || '').trim()} ${(d.user_lname || '').trim()}`.trim();
        if (name) return { name, avatar: d.profile_url?.trim() || '' };
      }
    } catch {}

    // Fallback: try numeric ID (e.g. "11")
    try {
      const numericId = extractUserId(memberId);
      const userDoc   = await getDoc(doc(db, FIREBASE_COLLECTIONS.USERS, numericId));
      if (userDoc.exists()) {
        const d    = userDoc.data();
        const name = `${(d.user_fname || '').trim()} ${(d.user_lname || '').trim()}`.trim();
        if (name) return { name, avatar: d.profile_url?.trim() || '' };
      }
    } catch {}

    return null;
  };

  const fetchMemberDetails = async (
    memberId: string,
    chatGroupData: any
  ): Promise<MemberInfo | null> => {
    try {
      const role      = determineRole(memberId, chatGroupData);
      const numericId = extractUserId(memberId);

      // Currently logged-in user → use localStorage for speed
      if (memberId === currentUserId) {
        try {
          const localUser = JSON.parse(localStorage.getItem('user') || '{}');
          const name      =
            `${(localUser.nutrist_fname || '').trim()} ${(localUser.nutrist_lname || '').trim()}`.trim() ||
            getFallbackName(memberId);
          return { id: memberId, name, role, avatar: localUser.nutrist_profile || '', userId: numericId };
        } catch {}
      }

      // Client → Firestore USERS collection
      if (memberId.startsWith('userid_')) {
        const result = await fetchClientFromFirestore(memberId);
        return {
          id:     memberId,
          name:   result?.name   || getFallbackName(memberId),
          role,
          avatar: result?.avatar || '',
          userId: numericId,
        };
      }

      // Nutritionist / Admin → backend API first, then Firestore fallback
      const apiResult = await fetchFromAPI(numericId);
      if (apiResult) {
        return { id: memberId, name: apiResult.name, role, avatar: apiResult.avatar, userId: numericId };
      }

      // Firestore fallback for nutritionist/admin
      try {
        const userDoc = await getDoc(doc(db, FIREBASE_COLLECTIONS.USERS, memberId));
        if (userDoc.exists()) {
          const d    = userDoc.data();
          const name = `${(d.user_fname || '').trim()} ${(d.user_lname || '').trim()}`.trim() || getFallbackName(memberId);
          return { id: memberId, name, role, avatar: d.profile_url?.trim() || '', userId: numericId };
        }
      } catch {}

      return { id: memberId, name: getFallbackName(memberId), role, avatar: '', userId: numericId };
    } catch (error) {
      console.error('❌ Error fetching member details for', memberId, error);
      return null;
    }
  };

  useEffect(() => {
    const loadMembers = async () => {
      if (!selectedChat?.id) { setMembers([]); return; }

      setLoading(true);
      try {
        const chatDoc = await getDoc(doc(db, FIREBASE_COLLECTIONS.CHAT_GROUPS, selectedChat.id as string));
        if (!chatDoc.exists()) return;

        const chatData   = chatDoc.data();
        const memberIds: string[] = chatData.members || [];

        const details = await Promise.all(memberIds.map((id) => fetchMemberDetails(id, chatData)));
        const valid   = details.filter((m): m is MemberInfo => m !== null);

        // Sort order: logged-in user first, then assigned nutritionist, then others
        const roleOrder: Record<string, number> = {
          'You (Assigned Nutritionist)': 0,
          'Assigned Nutritionist':       1,
          'You (Nutritionist)':          2,
          'Nutritionist':                3,
          'Client':                      4,
          'Head Nutritionist':           5,
          'Member':                      6,
        };

        valid.sort((a, b) => (roleOrder[a.role] ?? 99) - (roleOrder[b.role] ?? 99));
        setMembers(valid);
      } catch (error) {
        console.error('❌ Error loading members:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, [selectedChat?.id, currentUserId]);

  if (!selectedChat) return null;

  return (
    <div className="py-5 pl-5">
      <h6 className="text-sm font-semibold mb-4">Members ({members.length})</h6>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Icon icon="solar:loading-outline" height={30} className="text-gray-400 animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col">
          {members.map((member, index) => {
            // Divider: after the last nutritionist/admin, before Client
            const next        = members[index + 1];
            const showDivider =
              (member.role.includes('Nutritionist') || member.role === 'Head Nutritionist') &&
              next?.role === 'Client';

            return (
              <div key={member.id}>
                <div className="flex items-center gap-3 py-3">
                  {/* Avatar */}
                  {member.avatar ? (
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="w-10 h-10 rounded-full bg-[#DAF9F9] items-center justify-center flex-shrink-0"
                    style={{ display: member.avatar ? 'none' : 'flex' }}
                  >
                    <span className="text-sm font-semibold text-[#00A9A9]">
                      {getUserInitials(member.name)}
                    </span>
                  </div>

                  {/* Name + role */}
                  <div className="flex-1 min-w-0">
                    <h6 className="text-sm font-medium truncate">{member.name}</h6>
                    <p className="text-xs text-darklink truncate">{member.role}</p>
                  </div>
                </div>

                {showDivider && <div className="-mr-5 border-b border-border" />}
              </div>
            );
          })}

          {members.length === 0 && !loading && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No members found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatInsideSidebar;